import React, { useEffect, FunctionComponent } from "react";
import PropTypes from "prop-types";
import { connect, useSelector } from "react-redux";
import { Modal, Alert, Button } from "@xfrontend/xui-react-plus";
import { ThreatActivity } from "@xfrontend/xicon";
import { RootState } from "#/types/RootState";
import { CallbackFunction, CallbackFunctionVariadic } from "#/types/function";
import { cleanAxoisResultData } from "#/infra/redux/actionCreator";

const noop = () => {};

export interface CommonModalProps extends React.HTMLAttributes<HTMLElement> {
  title?: string;
  show: boolean | undefined;
  setShow?: CallbackFunctionVariadic<boolean>;
  cleanAxoisResultData?: CallbackFunction;
  onSubmit?: CallbackFunctionVariadic<unknown>;
  submitText?: string;
  submitButtonID?: string;
  submitVariant?: string;
  cancelText?: string;
  cancelButtonID?: string;
  hideCancelButton?: boolean;
  hidePrimaryButton?: boolean;
  disablePrimaryButton?: boolean;
  [x: string]: unknown;
}

const CommonModal: FunctionComponent<CommonModalProps> = ({
  show,
  setShow,
  cleanAxoisResultData: cleanAction,
  children,
  title,
  onSubmit,
  hideCancelButton,
  hidePrimaryButton,
  disablePrimaryButton,
  submitText,
  submitButtonID,
  submitVariant,
  cancelText,
  cancelButtonID,
  ...rest
}): JSX.Element => {
  const { axiosResult, axiosLoading } = useSelector(
    (state: RootState) => state.infra,
  );

  const onCancel = () => {
    setShow?.(false);
  };

  useEffect(() => {
    if (!axiosLoading && axiosResult?.success) {
      onCancel();
    }
  }, [axiosResult, axiosLoading]);

  useEffect(() => {
    cleanAction?.();
    return () => cleanAction?.();
  }, []);

  return (
    <Modal
      title={title}
      show={show}
      onClose={() => onCancel()}
      showCloseBtn
      onEscapeKeyDown={() => onCancel()}
      restoreFocus={false}
      {...rest}
    >
      {axiosResult?.msg && (
        <Alert className="modal-error" variant="warning">
          <ThreatActivity size={20} color="var(--x-gray-200)" />
          <div style={{ marginLeft: "8px", flex: "1" }}>
            <span>{axiosResult.msg}</span>
            {axiosResult?.detail && (
              <ul style={{ marginBottom: "0" }}>
                {Object.keys(axiosResult.detail).map((k) => (
                  <>
                    {Array.isArray(axiosResult.detail?.[k])
                      && axiosResult.detail?.[k].map((str) => <li key={str}>{str}</li>)}
                    {typeof axiosResult.detail?.[k] === "string"
                      && (<li key={k}>{axiosResult.detail?.[k]}</li>)}
                  </>
                ))}
              </ul>
            )}
          </div>
        </Alert>
      )}
      <Modal.Body>
        {children}
      </Modal.Body>
      <Modal.Footer className="buttonGroup d-flex align-items-center">
        {!hideCancelButton && (
          <Button
            id={cancelButtonID}
            variant="secondary"
            onClick={onCancel}
          >
            {cancelText}
          </Button>
        )}
        {!hidePrimaryButton && (
          <Button
            id={submitButtonID}
            disabled={axiosLoading || disablePrimaryButton}
            onClick={onSubmit}
            className="ms-2"
            variant={submitVariant}
          >
            {submitText}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

CommonModal.defaultProps = {
  title: "",
  show: false,
  setShow: noop,
  cleanAxoisResultData: noop,
  onSubmit: noop,
  submitText: "Submit",
  submitButtonID: "commonModal-submitButton",
  submitVariant: "primary",
  cancelText: "Cancel",
  cancelButtonID: "commonModal-closeButton",
  hideCancelButton: false,
  hidePrimaryButton: false,
  disablePrimaryButton: false,
};

CommonModal.propTypes = {
  title: PropTypes.string,
  show: PropTypes.bool,
  setShow: PropTypes.func,
  cleanAxoisResultData: PropTypes.func,
  onSubmit: PropTypes.func,
  submitText: PropTypes.string,
  submitVariant: PropTypes.string,
  cancelText: PropTypes.string,
  hideCancelButton: PropTypes.bool,
  hidePrimaryButton: PropTypes.bool,
  disablePrimaryButton: PropTypes.bool,
};

CommonModal.displayName = "CommonModal";

export default connect(() => ({}), {
  cleanAxoisResultData,
})(CommonModal);
